import {useCallback, useMemo, useState} from 'react';
import {useField, useFormikContext} from 'formik';
import {useGetDrawerSystemQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useNotificationContext} from 'contexts/NotificationContext';
import {Drawer} from 'components/customer/Product/entity/Drawer';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {
    DragLimits,
    keepWithin,
} from 'components/customer/Product/Drawer/helpers/useInnerDrawerDrag';

const POSITION = 'position_to_bottom';

const mm = (value: number) => String(Math.round(value * 100) / 100);

// pinned where it is is no room either, not a range of one number
const outsideLimits = (index: number, value: number, {min, max}: DragLimits) =>
    min >= max
        ? `${mm(value)}mm won't fit: drawer ${index + 1} has no room to move with the current runners.`
        : `${mm(value)}mm won't fit: drawer ${index + 1} can go from ${mm(min)} to ${mm(max)}mm with the current runners.`;

// Moving an inner drawer: it is taken off auto, and the height it gains or
// loses comes off the drawer below. Every way of moving one goes through here.
export const useDrawerPosition = (
    index: number,
    // where a move may go; without it, any position over 0 is taken
    limitsOf?: (index: number) => DragLimits
) => {
    const {addMessages} = useNotificationContext();
    const {setFieldValue} = useFormikContext();
    const [isInvalid, setIsInvalid] = useState(false);

    const name = `drawers[${index}].${POSITION}`;

    const [{value: drawers}] = useField<Drawer[]>({
        name: 'drawers',
    });
    const [, {error}] = useField<number>({name});

    const {data: drawerSystems} = useGetDrawerSystemQuery();

    const updateDrawerPosition = useCallback(
        (value: number) => {
            const drawer = drawers[Number(index)];

            if (drawer) {
                setIsInvalid(false);

                const difference = drawer.position_to_bottom - value;
                if (index == drawers.length - 1) {
                    const system = drawerSystems.find(
                        (system) => system.id == drawer.insert_id
                    );

                    if (system) {
                        void setFieldValue(
                            `drawers[${index}]`,
                            {
                                ...drawer,
                                [POSITION]: value,
                                touched: true,
                                drawer_face_height:
                                    drawer.drawer_face_height + difference,
                            },
                            true
                        );
                    } else {
                        return {
                            isValid: false,
                            message: `Cannot find drawer information.`,
                        };
                    }
                } else {
                    void setFieldValue(
                        `drawers[${index}]`,
                        {
                            ...drawer,
                            [POSITION]: value,
                            touched: true,
                            drawer_face_height:
                                drawer.drawer_face_height + difference,
                        },
                        true
                    );

                    const subsequentDrawer = drawers[Number(index) + 1];

                    if (subsequentDrawer) {
                        void setFieldValue(
                            `drawers[${Number(index) + 1}]`,
                            {
                                ...subsequentDrawer,
                                drawer_face_height:
                                    subsequentDrawer.drawer_face_height -
                                    difference,
                            },
                            true
                        );
                    }
                }

                return {isValid: true};
            }

            return {
                isValid: false,
                message: 'Invalid drawer position to bottom',
            };
        },
        [drawers, index, drawerSystems]
    );

    const updatePositionToBottom = useCallback(
        (_: string, value: number) => {
            if (value <= 0) {
                setIsInvalid(true);
                addMessages({
                    type: 'error',
                    messages: {
                        [index]: [
                            {
                                fields: [name],
                                message: 'Invalid drawer position to bottom.',
                            },
                        ],
                    },
                });

                return;
            }

            // the limits a drag stops at, so typing can't reach what dragging
            // can't; a drawer already outside may still come back in
            const limits = limitsOf?.(index);
            const current = toNumber(
                drawers?.[Number(index)]?.position_to_bottom
            );

            if (limits && keepWithin(value, current, limits) !== value) {
                setIsInvalid(true);
                addMessages({
                    type: 'error',
                    messages: {
                        [index]: [
                            {
                                fields: [name],
                                message: outsideLimits(index, value, limits),
                            },
                        ],
                    },
                });

                return;
            }

            const {isValid, message} = updateDrawerPosition(value);
            if (!isValid) {
                setIsInvalid(true);
                addMessages({
                    type: 'error',
                    messages: {
                        [index]: [
                            {
                                fields: [name],
                                message:
                                    message ||
                                    'Invalid drawer position to bottom.',
                            },
                        ],
                    },
                });
            }
        },
        [index, name, drawers, limitsOf, setFieldValue, updateDrawerPosition]
    );

    const positionValue = useMemo(() => {
        if (drawers) {
            const drawer = drawers[Number(index)];

            if (drawer && POSITION in drawer) {
                return Number(drawer.position_to_bottom);
            }
        }

        return 0;
    }, [drawers, index]);

    return {
        name,
        positionValue,
        isInvalid: isInvalid || error !== undefined,
        updatePositionToBottom,
    };
};
